import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, Input, Select, TabsList, TabsTrigger } from "@sagot/ui";
import { GAP_SORTS, GAP_TABS, type GapSort, type GapTab } from "./gap-model";

interface GapToolbarProps {
  counts: Partial<Record<GapTab, number>>;
  q: string;
  onQueryChange: (q: string) => void;
  sort: GapSort;
  onSortChange: (sort: GapSort) => void;
}

const TAB_LABEL: Readonly<Record<GapTab, string>> = {
  unanswered: "engage.gaps.tabs.unanswered",
  lowConfidence: "engage.gaps.tabs.lowConfidence",
  negative: "engage.gaps.tabs.negative",
  resolved: "engage.gaps.tabs.resolved",
};

export const isGapTab = (value: string): value is GapTab => GAP_TABS.some((tab) => tab === value);
const isSort = (value: string): value is GapSort => GAP_SORTS.some((sort) => sort === value);

/** Tab list (with counts) + search + sort row of the gap list card; render inside `<Tabs>`. */
export function GapToolbar({ counts, q, onQueryChange, sort, onSortChange }: GapToolbarProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 border-b border-line px-5 pt-2 lg:flex-row lg:items-center lg:justify-between">
      <TabsList className="overflow-x-auto border-b-0" aria-label={t("engage.gaps.tabs.label")}>
          {GAP_TABS.map((value) => {
            const count = counts[value];
            return (
              <TabsTrigger key={value} value={value} className="max-md:flex-col max-md:gap-1 max-md:text-center">
                {t(TAB_LABEL[value])}
                {count === undefined ? null : (
                  <Badge tone={value === "unanswered" && count > 0 ? "warning" : "neutral"} className="md:ml-1">
                    {count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
      </TabsList>
      <div className="flex items-center gap-2 pb-2 lg:pb-0">
        <div className="relative w-full lg:w-64">
          <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input
            type="search"
            className="h-9 pl-9"
            placeholder={t("engage.gaps.search")}
            aria-label={t("engage.gaps.search")}
            value={q}
            onChange={(event) => {
              onQueryChange(event.target.value);
            }}
          />
        </div>
        <Select
          className="h-9 w-40 shrink-0"
          aria-label={t("engage.gaps.sort.label")}
          value={sort}
          onChange={(event) => {
            if (isSort(event.target.value)) onSortChange(event.target.value);
          }}
        >
          {GAP_SORTS.map((value) => (
            <option key={value} value={value}>
              {t(`engage.gaps.sort.${value}`)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}
